import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Fade de entrada al cambiar de página — ADR-2026-07-31-fade-entre-paginas-toda-app.
 *
 * Es el mismo efecto que el shell de `/inicio` (ADR-2026-07-31-ruta-layout-shell-sidebar-deja y ADR-2026-07-31-ajustes-shell-padding-secciones-hospedadas) llevado a toda la app.
 * Devuelve `true` cuando la página tiene que estar visible y `false` durante el
 * frame en que se acaba de montar, para que el consumidor haga
 * `opacity-0 → opacity-100` con una transición.
 *
 * **Sólo entrada.** En `/inicio` el fade también tiene salida porque toda la
 * navegación pasa por un único lugar (los ítems de la sidebar), y ahí se puede
 * apagar el contenido ANTES de navegar. En el resto de la app hay `navigate()`
 * repartido por decenas de pantallas, más el back del navegador: cuando el
 * `pathname` cambia, la pantalla vieja ya no existe y no hay nada que desvanecer.
 * Un cross-fade real exigiría mantener montado el árbol saliente, que en pantallas
 * como el historial son miles de nodos y videos reproduciéndose.
 *
 * El `useLayoutEffect` importa: apaga la opacidad **antes** del primer pintado. Con
 * un `useEffect` normal el navegador alcanza a pintar la pantalla nueva a opacidad
 * 1 y recién después la apaga — un parpadeo al revés.
 */
export function usePageFade(): boolean {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(true);

  useLayoutEffect(() => {
    setVisible(false);
  }, [pathname]);

  useEffect(() => {
    // Doble rAF: el primero garantiza que el navegador ya commiteó el estado
    // apagado; el segundo dispara el encendido para que CSS detecte la
    // transición. Con uno solo, React batchea los dos cambios en el mismo commit
    // y la animación se saltea (mismo motivo que en `useExitTransition`).
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [pathname]);

  return visible;
}

/**
 * Clases del fade, para que todos los consumidores usen exactamente la misma
 * curva y duración. Se aplican sobre un elemento que YA existe (el panel de
 * contenido), no sobre un wrapper nuevo: un nodo extra cambiaría el bloque
 * contenedor de las alturas en porcentaje de las pantallas de adentro.
 */
export function clasesPageFade(visible: boolean): string {
  return visible ? 'page-fade-in' : 'opacity-0';
}
